import { Link } from "react-router-dom";
import { Check, Ban } from "lucide-react";

export default function PricingRules() {
  return (
    <div className="mx-auto max-w-4xl px-5 py-14 lg:px-8 lg:py-20">
      <div className="text-xs uppercase tracking-widest-xs text-[hsl(var(--brand))]">Pricing & licensing</div>
      <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
        How we price and licence.
      </h1>
      <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
        BIGTEK engagements are proposal-based. Published prices are indicative starting points — the
        final figure depends on scope, data sensitivity and authority requirements.
      </p>

      <div className="mt-12 space-y-px border border-border bg-border">
        <Section title="Indicative pricing">
          <p>
            Every "From" price is a starting point, <strong>subject to scope and exclusive of GST</strong>.
            No payments are taken on this website — all engagements proceed by written proposal.
          </p>
        </Section>

        <Section title="Government & enterprise">
          <p>
            Government agencies, enterprises and well-resourced institutions are quoted at full price
            against a defined scope.
          </p>
        </Section>

        <Section title="Community-controlled pathway">
          <p>
            Nations, Indigenous organisations and community-controlled entities may access a
            <strong> subsidised pathway — priced on application</strong>. This requires separate approval
            and is not published as a fixed figure. <Link to="/catalogue" className="underline">Contact us</Link> to
            discuss eligibility.
          </p>
        </Section>

        <Section title="40 / 30 / 30 payment structure">
          <p>Engagements are typically invoiced across three milestones:</p>
          <ul className="mt-4 space-y-2">
            {[
              ["40%", "on commencement and scope confirmation"],
              ["30%", "at the agreed midpoint / draft delivery"],
              ["30%", "on final delivery and acceptance"],
            ].map(([pct, desc], i) => (
              <li key={`${pct}-${i}`} className="flex items-start gap-3">
                <span className="font-mono text-sm font-medium text-[hsl(var(--brand))]">{pct}</span>
                <span>{desc}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Controlled licences">
          <p>Framework and toolkit products are supplied under project-specific controlled licences. Each licence explicitly:</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {[
              ["allow", "Defines the project, authorised users and term"],
              ["allow", "Sets the authority pathway and evidence requirements"],
              ["allow", "Includes withdrawal and exit controls"],
              ["deny", "No editable master distributed"],
              ["deny", "No sublicensing or resale"],
              ["deny", "No AI training"],
              ["deny", "No unrestricted or out-of-scope reuse"],
            ].map(([type, text], i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {type === "allow" ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={1.75} />
                ) : (
                  <Ban className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={1.75} />
                )}
                <span className="text-muted-foreground">{text}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Pilots are not production">
          <p>
            Paid pilots and the Governance Engine MVP run on synthetic, public or expressly authorised
            data for one approved use case. <strong>Pilot rights do not imply production rights, perpetual
            access or ongoing hosting.</strong> Any production or enterprise licence is defined separately.
          </p>
        </Section>

        <Section title="Roadmap products">
          <p>
            Roadmap concepts show <strong>Register Interest</strong> only. No pricing is published until a
            product moves into an available tier.
          </p>
        </Section>
      </div>

      <p className="mt-10 text-xs leading-relaxed text-slate-500">
        All figures are indicative, subject to scope and exclude GST. This page is not a quote, offer or
        legal advice. Trade marks are used as claimed; entity and IP status subject to confirmation.
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-background p-6 lg:p-8">
      <h2 className="font-display text-xl font-medium">{title}</h2>
      <div className="mt-3 text-base leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}
